<script setup>
import { getActivePinia, storeToRefs, } from 'pinia'
import { useUserStore, } from '@/assets/js/src/modules/user/_pinia/user'
import { useAppUiStore, } from '@/assets/js/src/pinia/appUi'
import { useBugReportStore, } from '@/assets/js/src/modules/report/_pinia/bugreport'
import { useSnackbarStore, } from '@/assets/js/src/modules/snackbar/_pinia/snackbar'
import { usePrefetchData, } from '@/assets/js/src/util/composables/usePrefetchData'
import { useTranslation, } from '@/assets/js/src/util/composables/useTranslation'
import { computed, nextTick, onUnmounted, ref, watch, } from 'vue'
import { REPORT_TYPES, } from '@/assets/js/src/modules/report/_pinia/_constants.js'
import { useTrapKeyboard, } from '@/assets/js/src/util/composables/useTrapKeyboard.js'

let { messagesLoaded, } = usePrefetchData({
    loadAsyncMessages: [ 'bugreport', 'common', 'user', ],
})

const { t, } = useTranslation()
const { trapKeyboard, } = useTrapKeyboard('.bugreport-wrapper')
const activePinia = getActivePinia()
const userStore = useUserStore(activePinia)
const appUiStore = useAppUiStore(activePinia)
const { report, reportType, } = storeToRefs(appUiStore)
const bugReportStore = useBugReportStore(activePinia)
const snackbarStore = useSnackbarStore(activePinia)

const form = ref(null)
const type = ref(reportType.value)
const text = ref('')
const subject = ref('')
const email = ref(userStore.email)
const animate = ref(false)
const valid = ref(false)
const loading = ref(false)

const items = computed(() => messagesLoaded.value ?
    Object.keys(REPORT_TYPES).map((value) => (
        {
            title: t(REPORT_TYPES[value]),
            value,
        }
    )) : [])

watch(messagesLoaded, (newVal) => {
    if (newVal) {
        animate.value = true
        trapKeyboard(true, false, () => {
            report.value = false
        })
    }
}, { immediate: true, })


const reset = () => {
    type.value = ''
    text.value = ''
    subject.value = ''
    loading.value = false
    report.value = false
}

const handleBugReport = async () => {
    loading.value = true

    const reportData = {
        text: globalThis.clone(text.value),
        type: globalThis.clone(type.value),
        subject: globalThis.clone(subject.value),
        email: globalThis.clone(email.value),
    }

    if (valid.value) {
        bugReportStore.sendBugReport(reportData)

        snackbarStore.showMessage({
            message: t('async.bugreport.message'),
            type: 'success',
        })

        reset()
    } else {
        loading.value = false
    }
}

onUnmounted(() => {
    if (!import.meta.env.SSR) {
        animate.value = false
        nextTick(() => {
            trapKeyboard(false,true, ()=>{
                report.value = false
            })
        })
    }
})

</script>

<template>
    <div
        class="bugreport-wrapper"
    >
        <transition name="bugreport-wrapper-scale">
            <div
                v-if="animate && messagesLoaded"
                class="bugreport-wrapper__form-wrapper"
            >
                <div
                    id="bugreport-wrapper-form"
                    class="bugreport-wrapper__form-wrapper__form"
                    @click.stop
                >
                    <h2>
                        {{ t('async.bugreport.report') }}
                    </h2>
                    <v-form
                        ref="form"
                        v-model="valid"
                    >
                        <v-select
                            v-model="type"
                            :items="items"
                            :rules="[v => !!v || t('async.bugreport.required') ]"
                            :label="t('async.bugreport.type')"
                            bg-color="background-light"
                            :menu-props="{attach:'.bugreport-wrapper',zIndex: 10000,}"
                            required
                        ></v-select>

                        <v-text-field
                            v-model="subject"
                            :label="t('async.bugreport.subject')"
                            bg-color="background-light"
                            :rules="[v => !!v || t('async.bugreport.required')]"
                        ></v-text-field>

                        <v-text-field
                            v-model="email"
                            bg-color="background-light"
                            :label="t('async.bugreport.email')"
                            :rules="[v => !!v || t('async.user.form.email.error.required'), v => !v || typeof v === 'string' && /^.+@\S+\.\S+$/.test(v) || t('async.user.form.email.error.valid')]"
                        ></v-text-field>

                        <v-textarea
                            v-model="text"
                            bg-color="background-light"
                            :rules="[v => !!v || t('async.bugreport.required')]"
                            :label="t('async.bugreport.description')"
                        ></v-textarea>

                        <div class="bugreport-wrapper__form-wrapper__form__button-group">
                            <v-btn
                                variant="text"
                                :disabled="loading"
                                @click="reset()"
                            >
                                {{ t('async.common.button.cancel') }}
                            </v-btn>

                            <v-btn
                                color="primary"
                                :disabled="!valid"
                                :loading="loading"
                                @click="handleBugReport()"
                            >
                                {{ t('async.bugreport.submit') }}
                            </v-btn>
                        </div>
                    </v-form>
                </div>
            </div>
        </transition>
    </div>
</template>

<style lang="scss">
    .bs-app .bugreport-wrapper {
        &-scale-enter-from, &-scale-list-leave-to {
            transform: scale(1, 0);
            transform-origin: top center;
            opacity: 0;
        }

        &-scale-enter-active, &-scale-leave-active {
            transition: .3s cubic-bezier(.25, .8, .5, 1);
        }

        &-scale-enter-to, &-scale-leave-from {
            transform: scale(1, 1);
            transform-origin: top center;
            opacity: 1;
        }

        &__form-wrapper {
            position: fixed;
            z-index: 9999;
            width: 100vw;
            height: 100vh;
            background: rgb(0 0 0 / 50%);

            &__form {
                position: absolute;
                top: 50%;
                left: 50%;
                z-index: inherit;
                width: 100%;
                max-width: 600px;
                max-height: calc(100vh - 12px);
                padding: 25px;
                overflow: scroll;
                background: var(--bs-color-white);
                transform: translate(-50%, -50%);

                &__button-group {
                    display: flex;
                    justify-content: flex-end;
                }
            }
        }
    }
</style>
